import React, { useEffect, useState } from "react";
import { fetchProducts } from "./api";
import { Spin, Pagination } from "antd";
import { motion } from "framer-motion";
import "./homePage.css";
import Navbar from "../../components/Navbar";
import { useNavigate } from "react-router-dom";
import { LogoMarque } from "../../components/LogoMarque";
import PartnerLogos from "./PartnerLogos";
import WhatsAppFloatButton from "../../components/DialMenu/WhatsappFloatButton";

const HomePage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = [
    { label: "Chemical", value: "CO1" },
    { label: "Laboratory Equipment", value: "C02" },
    { label: "Mining Equipment", value: "C03" },
  ];

  useEffect(() => {
    setLoading(true);
    fetchProducts(selectedCategory)
      .then(({ data = [] }) => {
        const dataWithStock = data.map((product) => ({
          ...product,
          stock: Math.floor(Math.random() * 50) + 1,
        }));
        setProducts(dataWithStock || []);
      })
      .finally(() => setLoading(false));
  }, [selectedCategory]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleProducts = products.slice(startIndex, endIndex);

  return (
    <>
      <Navbar />
      <motion.div
        className="home-page min-h-screen text-[#5c4033] font-serif p-8 pt-28 pb-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {loading ? (
          <div className="flex justify-center items-center min-h-[50vh]">
            <Spin
              size="large"
              tip="Loading products..."
              className="custom-spin"
            />
          </div>
        ) : (
          <>
            <LogoMarque />
            <PartnerLogos />

            <motion.h1
              className="text-4xl font-bold mb-10 drop-shadow-lg text-center"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              Our Products
            </motion.h1>

            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* Sidebar */}
              <div className="md:col-span-3">
                <div className="bg-white rounded-xl shadow-md border p-4">
                  <h3 className="text-xl font-bold text-[#5c4033] mb-4">
                    Browse Categories
                  </h3>
                  <ul className="flex flex-col gap-3">
                    <li
                      onClick={() => {
                        setSelectedCategory(null);
                        setCurrentPage(1);
                      }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-[#f3efea] ${
                        selectedCategory === null
                          ? "bg-[#f3efea] font-semibold text-[#5c4033]"
                          : "text-gray-600"
                      }`}
                    >
                      <span>📦</span>
                      All Products
                    </li>

                    {categories.map((cat) => {
                      const iconMap = {
                        CO1: "🧪", // Chemical
                        C02: "🧫", // Lab Equipment
                        C03: "⛏️", // Mining
                      };

                      return (
                        <li
                          key={cat.value}
                          onClick={() => {
                            setSelectedCategory(cat.value);
                            setCurrentPage(1);
                          }}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-[#f3efea] ${
                            selectedCategory === cat.value
                              ? "bg-[#f3efea] font-semibold text-[#5c4033]"
                              : "text-gray-600"
                          }`}
                        >
                          <span>{iconMap[cat.value]}</span>
                          {cat.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Product Cards */}
              <div className="md:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleProducts.length === 0 ? (
                  <div className="col-span-full text-center text-lg font-medium text-gray-500">
                    Products Not Available...
                  </div>
                ) : (
                  visibleProducts.map((product) => (
                    <div
                      key={product.id}
                      className="product-card p-4 border rounded-lg shadow-md flex flex-col cursor-pointer"
                      onClick={() => navigate(`/catalog/${product.id}`)}
                    >
                      <img
                        src={product.thumbnail}
                        alt={product.title}
                        className="product-image mb-4 mx-auto max-h-40 object-contain"
                      />
                      <h2 className="text-lg font-semibold mb-2">
                        {product.title}
                      </h2>
                      <p className="text-sm flex-grow">
                        {product.description.substring(0, 100)}...
                      </p>
                      <div className="mt-4 flex justify-between items-center text-sm">
                        <span className="font-bold text-[#5c4033]"></span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/catalog/${product.id}`);
                          }}
                          className="text-white bg-[#5c4033] px-3 py-2 text-xs rounded-md hover:bg-[#4a3328]"
                        >
                          Detail
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {products.length > 0 && (
              <div className="flex justify-center mt-10">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={products.length}
                  onChange={(page, newPageSize) => {
                    setPageSize(newPageSize);
                    setCurrentPage(newPageSize !== pageSize ? 1 : page);
                  }}
                  showSizeChanger={true}
                  pageSizeOptions={["5", "10", "20", "50"]}
                />
              </div>
            )}
          </>
        )}

        <div className="block md:hidden min-h-[300px]"></div>
      </motion.div>
      <WhatsAppFloatButton />
    </>
  );
};

export default HomePage;
